import ApiService, { HttpMethod } from "../../../../commons/api/api_service";
import BaseResponse from "../../../../commons/api/base_response";
import { Endpoints } from "../../../../commons/api/endpoints";
import { ProviderVM } from "../../../../commons/base/providers/view_model";
import { ShowSnackbarModel } from "../../../../commons/scaffold/scaffold";
import { ItemQuantityModel } from "../../commons/models/item_quantity_model";
import LocationModel from "../../commons/models/location_model";
import MasterItemModel from "../../commons/models/master_item_model";
import { AdjustableSOFormResponse } from "../models/adjustable_so_form_response";
import SoAdjustmentSubmitRequest from "../models/so_adjustment_submit_request";
import SOAdjustmentFormDetailResponse from "../models/so_form_detail_response";
import SOAdjustmentPreloadingResponse from "../models/so_preloading_response";

export default class SOAdjustmentViewModel extends ProviderVM {
    isLoading: boolean = false;
    snackBar?: ShowSnackbarModel;

    locations: LocationModel[] = [];
    forms: AdjustableSOFormResponse[] = [];
    masterItemMap: Map<number, MasterItemModel> = new Map<
        number,
        MasterItemModel
    >();

    // form data
    isShowingModal: boolean = false;
    searchQuery: string = "";
    selectedItemId: number = 0;
    formId?: number;
    formCreatedDate?: string;
    formItemsMap: Map<number, ItemQuantityModel> = new Map<
        number,
        ItemQuantityModel
    >();

    get shownMasterItems() {
        const masterItemIdsInForm = Array.from(this.formItemsMap.values()).map(
            (item) => item.masterItemId
        );

        return Array.from(this.masterItemMap.values()).filter((item) =>
            masterItemIdsInForm.includes(item.id)
        );
    }

    get filteredMasterItems() {
        return this.shownMasterItems.filter((item) =>
            item.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
    }

    updateSearchQuery(value: string) {
        this.emit(() => {
            this.searchQuery = value;
        });
    }

    updateFormCreatedDate(value: string) {
        this.emit(() => {
            this.formCreatedDate = value;
        });
    }

    updateSelectedItemId(value: number) {
        this.emit(() => {
            this.selectedItemId = value;
        });
    }

    constructor() {
        super();
        this.fetchSOPreloading();
    }

    fetchSOPreloading() {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(Endpoints.newSOAdjustmentPreloading, {
            httpMethod: HttpMethod.get,
            onSuccess: (
                response: BaseResponse<SOAdjustmentPreloadingResponse>
            ) => {
                if (response.code === 200) {
                    const masterItemMap = new Map<number, MasterItemModel>();

                    response.data?.items.forEach((item) => {
                        masterItemMap.set(item.id, item);
                    });

                    this.emit(() => {
                        this.locations = response.data?.locations ?? [];
                        this.forms = response.data?.forms ?? [];
                        this.masterItemMap = masterItemMap;
                        this.isLoading = false;
                    });
                } else {
                    this.emit(() => {
                        this.snackBar = {
                            key: Date.now().toString(),
                            message: response.message,
                            type: "error",
                        };
                        this.isLoading = false;
                    });
                }
            },
            onFailure: (error, errorCode) => {
                this.emit(() => {
                    this.snackBar = {
                        key: Date.now().toString(),
                        message: error,
                        type: "error",
                    };
                    this.isLoading = false;
                });
            },
        });
    }

    fetchSOList(locationId: number) {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(
            `${Endpoints.newSOAdjustmentGetForms}/${locationId}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (
                    response: BaseResponse<AdjustableSOFormResponse[]>
                ) => {
                    if (response.code === 200) {
                        this.emit(() => {
                            this.isLoading = false;
                            this.forms = response.data ?? [];
                        });
                    } else {
                        this.emit(() => {
                            this.snackBar = {
                                key: Date.now().toString(),
                                message: response.message,
                                type: "error",
                            };
                            this.isLoading = false;
                        });
                    }
                },
                onFailure: (error, errorCode) => {
                    this.emit(() => {
                        this.snackBar = {
                            key: Date.now().toString(),
                            message: error,
                            type: "error",
                        };
                        this.isLoading = false;
                    });
                },
            }
        );
    }

    fetchSODetail(soId: number) {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(
            `${Endpoints.newSOAdjustmentGetFormDetail}/${soId}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (
                    response: BaseResponse<SOAdjustmentFormDetailResponse>
                ) => {
                    if (response.code === 200) {
                        const responseData: SOAdjustmentFormDetailResponse =
                            response.data as SOAdjustmentFormDetailResponse;

                        const formItemsMap: Map<number, ItemQuantityModel> =
                            new Map<number, ItemQuantityModel>();

                        response.data?.items.forEach((item) => {
                            formItemsMap.set(item.masterItemId, item);
                        });

                        this.emit(() => {
                            this.isLoading = false;
                            this.isShowingModal = true;
                            this.formId = responseData.formId;
                            this.formCreatedDate = responseData.createdDate;
                            this.formItemsMap = formItemsMap;
                        });
                    } else {
                        this.emit(() => {
                            this.snackBar = {
                                key: Date.now().toString(),
                                message: response.message,
                                type: "error",
                            };
                            this.isLoading = false;
                        });
                    }
                },
                onFailure: (error, errorCode) => {
                    this.emit(() => {
                        this.snackBar = {
                            key: Date.now().toString(),
                            message: error,
                            type: "error",
                        };
                        this.isLoading = false;
                    });
                },
            }
        );
    }

    submitSOAdjustment() {
        this.emit(() => {
            this.isLoading = true;
        });

        const submitParams: SoAdjustmentSubmitRequest = {
            createdDate: this.formCreatedDate ?? "",
            items: Array.from(this.formItemsMap.values())
                .filter((item) => item.isUpdated)
                .map((item) => ({
                    masterItemId: item.masterItemId,
                    qtyBigUnit: item.qtyBigUnit ?? 0,
                    qtySmallUnit: item.qtySmallUnit ?? 0,
                })),
        };

        ApiService.fetch(
            `${Endpoints.newSOAdjustmentSubmit}/${this.formId}` as Endpoints,
            {
                httpMethod: HttpMethod.post,
                parameters: submitParams,
                onSuccess: (response: BaseResponse<{}>) => {
                    if (response.code === 200) {
                        this.emit(() => {
                            this.isLoading = false;
                            this.snackBar = {
                                key: Date.now().toString(),
                                message: response.message,
                                type: "normal",
                            };
                            this.isShowingModal = false;
                        });

                        this.fetchSOPreloading();
                    } else {
                        this.emit(() => {
                            this.snackBar = {
                                key: Date.now().toString(),
                                message: response.message,
                                type: "error",
                            };
                            this.isLoading = false;
                        });
                    }
                },
                onFailure: (error, errorCode) => {
                    this.emit(() => {
                        this.snackBar = {
                            key: Date.now().toString(),
                            message: error,
                            type: "error",
                        };
                        this.isLoading = false;
                    });
                },
            }
        );
    }

    addItemQtyModel(masterItemId: number) {
        const updatedFormItemsMap = new Map(this.formItemsMap);

        if (!updatedFormItemsMap.get(masterItemId)) {
            updatedFormItemsMap.set(masterItemId, {
                masterItemId: masterItemId,
                qtyBigUnit: 0,
                qtySmallUnit: 0,
                isUpdated: true,
            });
        }

        this.emit(() => {
            this.formItemsMap = updatedFormItemsMap;
            this.selectedItemId = 0;
        });
    }

    removeItemQtyModel(masterItemId: number) {
        const updatedFormItemsMap = new Map(this.formItemsMap);
        updatedFormItemsMap.delete(masterItemId);
        this.emit(() => {
            this.formItemsMap = updatedFormItemsMap;
        });
    }

    updateItemQtyModel(
        masterItemId: number,
        field: keyof ItemQuantityModel,
        value: number
    ) {
        const updatedFormItemsMap = new Map(this.formItemsMap);
        const oldItemQtyModel = updatedFormItemsMap.get(masterItemId);

        if (oldItemQtyModel) {
            updatedFormItemsMap.set(masterItemId, {
                ...oldItemQtyModel,
                isUpdated: true,
                [field]: value,
            });
        }

        this.emit(() => {
            this.formItemsMap = updatedFormItemsMap;
        });
    }

    resetForm() {
        this.emit(() => {
            this.isShowingModal = false;
            this.formId = undefined;
            this.formCreatedDate = undefined;
            this.formItemsMap = new Map<number, ItemQuantityModel>();
            this.selectedItemId = 0;
        });
    }
}
